import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {StorageService} from './storage-service';
import {Logger} from './logger';
import {BehaviorSubject, distinctUntilChanged} from 'rxjs';
import {NetworkInfoService} from './network-info-service';
import {ConfigService} from './config/config-service';
import {getGlobalScope} from '@sentry/browser';
import {EventsService} from './events-service';
import {getText} from './utils/get-text';
import {simplifyMacAddress} from './utils/simplify-mac-address';

@Injectable()
export class RoomNumberService implements AppInitializeService {
  private readonly storageService = inject(StorageService);
  private readonly logger = inject(Logger);
  private readonly networkInfoService = inject(NetworkInfoService);
  private readonly configService = inject(ConfigService);

  public readonly roomNumber$ = new BehaviorSubject<string | undefined>(undefined);

  /**
   * Get current room number
   */
  public get roomNumber() {
    return this.roomNumber$.getValue();
  }

  constructor() {
    this.roomNumber$.pipe(distinctUntilChanged()).subscribe((roomNumber) => {
      this.logger.debug(`[RoomNumberService] Setting room tag for Sentry ${roomNumber}`);
      getGlobalScope().setTag('room', roomNumber);
    });
  }

  public async init(): Promise<boolean> {
    try {
      let roomNumber: string | undefined;
      if (this.configService.config.tv?.autoconfigure) {
        roomNumber = await this.autoconfigureRoomNumber();
      }

      roomNumber = roomNumber || this.storageService.getString('room');
      if (!roomNumber || roomNumber == '0') {
        this.logger.debug('[App] unable to find saved room number');
        this.ask_for_room();
      } else {
        window.tv_room = roomNumber;
        this.roomNumber$.next(roomNumber);
        this.logger.log(`[App] Room number: ${roomNumber}`);
      }

      return Promise.resolve(true);
    } catch (err) {
      this.logger.critical('[RoomNumberService] Unable to initialize room number', err);
      return Promise.resolve(false);
    }
  }

  private async autoconfigureRoomNumber(): Promise<string | undefined> {
    this.logger.debug('[App] room number autoconfiguration...');
    let mac = this.networkInfoService.networkInfo$.getValue()?.mac;
    if (!mac) {
      this.logger.warn(
        "[RoomNumberService] The `config.tv.autoconfigure` setting is enabled, but we were unable to determine the TV's MAC address. Therefore, automatic configuration of the room number and TVID is not possible.",
      );
      return;
    }

    mac = simplifyMacAddress(mac);

    // checking the config mapping
    try {
      const configMapping = this.configService.config.tv?.macRoomMapping;
      if (configMapping && Array.isArray(configMapping)) {
        const mapping = configMapping.find((m) => m.mac && simplifyMacAddress(m.mac) === mac);
        if (mapping && mapping.room) {
          if (typeof mapping.room === 'number') {
            // If someone confuses the type in the config.
            mapping.room = (mapping.room as number).toString();
          }
          this.applyRoomNumberAndTvid(mapping.room, mapping.tvid);
          return mapping.room;
        } else {
          this.logger.debug(`[RoomNumberService] mac address ${mac} was not found in config mapping`);
        }
      }
    } catch (err) {
      this.logger.error('[RoomNumberService] TV mapping config (config.tv.mapping) processing error: ', err);
    }

    // checking the 'custom/tv_mapping' file mapping
    try {
      this.logger.debug(`[RoomNumberService] getting TV mapping file "custom/tv_mapping"...`);

      const tvMappingData = await getText('custom/tv_mapping');
      const mapping = this.parseTvMappingFile(tvMappingData, mac);

      if (mapping && mapping.room) {
        this.applyRoomNumberAndTvid(mapping.room, mapping.tvid);
        return mapping.room;
      } else {
        this.logger.debug(`[RoomNumberService] mac address ${mac} was not found in TV mapping file`);
      }
    } catch (err) {
      this.logger.warn('[RoomNumberService] TV mapping file processing error: ', err);
    }
  }

  /**
   * Parse TV mapping file
   * Format: ${MAC} ${room} ${tvid}
   * Elements can be wrapped in quotes
   * @param data file content
   * @param tvMacAddress MAC address to find
   * @private
   */
  private parseTvMappingFile(data: string, tvMacAddress: string): {room: string; tvid?: string} | undefined {
    if (!data) {
      return undefined;
    }

    const lines = data
      .replace(/\r\n/g, '\n')
      .replace(/[ \t]+/g, ' ')
      .split('\n')
      .filter((r) => !!r);

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }

      // Regular expression to match space/tab separated values that can be quoted
      // This regex matches:
      // 1. Double quoted string: "([^"]*)"
      // 2. Single quoted string: '([^']*)'
      // 3. Non-quoted string: ([^\s"']+)
      const regex = /"([^"]*)"|'([^']*)'|([^\s"']+)/g;
      const parts: string[] = [];
      let match;

      while ((match = regex.exec(line)) !== null && parts.length < 3) {
        // match[1] is double quoted, match[2] is single quoted, match[3] is non-quoted
        parts.push(match[1] || match[2] || match[3] || '');
      }

      if (parts.length >= 2 && parts[0] && simplifyMacAddress(parts[0]) === tvMacAddress) {
        return {
          room: parts[1],
          tvid: parts[2] || undefined,
        };
      }
    }

    return undefined;
  }

  private applyRoomNumberAndTvid(room: string, tvid?: number | string) {
    this.storageService.set('room', room);
    window.tv_room = room;
    this.roomNumber$.next(room);

    const eventsService = inject(EventsService);
    eventsService.forceSetTvid(tvid);

    this.logger.debug(
      `[RoomNumberService] Room number autoconfigured to room: ${room}, TVID: ${eventsService.TVID()}.`,
    );
  }

  private ask_for_room() {
    this.logger.debug('[App] Asking for room number...');
    if (window.system_started) {
      window.tv_configure();
    } else if (!this.configService.config.tv?.configure_hide) {
      $(window.HotezaTV).one('final', () => {
        setTimeout(() => {
          window.tv_configure();
        }, 1000);
      });
    }
  }
}

declare global {
  interface Window {
    /**
     * Room number
     */
    tv_room: string;
  }
  const tv_room: string;
}
